import Section from '@widgets/components/section';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import UserIndicator from '@widgets/components/user-indicator';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { InputCheckbox } from '@shared/uikit/input-checkbox';
import IconDownload from '@shared/icons/IconDownload';
import { BlueTransparentButton, GrayButton, RedTransparentButton } from '@shared/uikit/button';
import { ISelectOption } from '@widgets/multi-select';
import { ListLoader, Loader } from '@shared/uikit/loader';
import SectionHeader from '@widgets/components/section-header';
import CustomRangeDatePicker from '@widgets/range-date-picker-with-modes';
import { selectCurrentUser, selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import EmptyList from '@widgets/empty-list';
import SingleSelect from '@widgets/single-select';
import { useUpdateCustomer } from '@app/queries/customer/useUpdateCustomer';
import { toast } from 'react-toastify';
import useCustomerOptions from '@pages/Customer/hooks/useCustomerOptions';
import useCustomDateShortcuts from '@app/hooks/useCustomDateShortcuts';
import { TextInput } from '@shared/uikit/input-text';
import { toIsoDate } from '@app/utils/date';
import IconVerticalThreeDots from '@shared/icons/IconVerticalThreeDots';
import DropdownButton from '@widgets/dropdown-button';
import { DeletePrompt } from '@widgets/prompt';
import IconKanbanUser from '@shared/icons/IconKanbanUser';
import { useAccount } from '@app/queries/account/useAccount';
import { ACCOUNT_TYPE, IAccount } from '@entities/account/IAccount';
import useAccountBreadcrumbs from '@pages/Account/hooks/useAccountBreadcrumbs';
import { useEditAccount } from '@app/queries/account/useEditAccount';
import { useDeleteAccount } from '@app/queries/account/useDeleteAccount';
import useAccountGraph from '@pages/Account/hooks/useAccountGraph';
import AccountLinesChart from '@pages/Account/components/line-chart';
import IconWrapper from '@shared/uikit/IconWrapper';
import { ServerError } from '@app/error/ServerError';
import { ACCOUNT_PERMISSIONS } from '@app/contstants/permissions';
import PermissionGuard from '@shared/security/PermissionGuard';
import { useUnsavedChanges } from '@app/hooks/useUnsavedChanges';
import { ToggleSwitch } from '@shared/uikit/toggle-switch';
import { useSwitchAccountIsNew } from '@app/queries/account/useSwitchAccountIsNew';
import { Tooltip } from 'react-tooltip';
import { resolveAccountTypeTitle } from '@pages/Account/helpers/accountTypeHelper';
import ChangeAccountTypePopup, {
  IChangeAccountTypeValues,
} from '@pages/Account/Show/ChangeAccountTypePopup';

const AccountShow = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /*
        Selectors
     */
  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);
  const user = useSelector(selectCurrentUser);
  const isAdmin = user?.user?.is_admin === true;

  /*
        Fetchers
     */
  const accountFetcher = useAccount();
  const editAccountFetcher = useEditAccount();
  const customerUpdateFetcher = useUpdateCustomer();
  const deleteAccountFetcher = useDeleteAccount();
  const switchIsNewFetcher = useSwitchAccountIsNew();

  /*
        State
     */
  const [account, setAccount] = useState<IAccount | null>(null);

  const [overrideName, setOverrideName] = useState<string>('');
  const [tags, setTags] = useState<ISelectOption | null>(null);
  const [sources, setSources] = useState<ISelectOption | null>(null);
  const [managers, setManagers] = useState<ISelectOption[] | null>(null);
  const [accountType, setAccountType] = useState<ISelectOption | null>(null);
  const [clientOutOfFlex, setClientOutOfFlex] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isShownChangeTypePopup, showChangeTypePopup] = useState(false);

  const [filters, setFilters] = useState<{
    dateFrom: string;
    dateTo: string;
  } | null>(null);

  const [isShowDeleteAccounts, setShowDeleteAccounts] = useState<boolean>(false);
  /*
        Hooks
     */
  useAccountBreadcrumbs(account);
  useUnsavedChanges(isEditing);

  const [loadingGraphData, graphData] = useAccountGraph(id, filters);

  const referralCashbackTotal = useMemo(() => {
    if (account?.account_type !== ACCOUNT_TYPE.REFERRAL || !account.agents.length) {
      return null;
    }

    return account.agents.reduce((sum, agent) => sum + Number(agent.cashback || 0), 0);
  }, [account]);

  const [loadingOptions, sourceOptions, managerOptions, tagOptions] = useCustomerOptions();

  const [dateShortcuts, defaultDateFrom, defaultDateTo] = useCustomDateShortcuts();

  const accountActions: ISelectOption[] = useMemo(() => {
    const actions: ISelectOption[] = [{ value: 'edit', label: 'Редактировать' }];
    if (!account?.account_type) {
      actions.push({ value: 'change_type', label: 'Назначить тип' });
    }
    actions.push({ value: 'remove', label: 'Удалить' });
    return actions;
  }, [account?.account_type]);

  const handleAccountAction = (value: string) => {
    switch (value) {
      case 'edit':
        setEditing(true);
        break;
      case 'change_type':
        if (!account?.account_type) {
          showChangeTypePopup(true);
        }
        break;
      case 'remove':
        setShowDeleteAccounts(true);
        break;
      default:
        return;
    }
  };

  /*
        Effects
     */

  useEffect(() => {
    (async () => {
      if (!id) return;

      const account = await accountFetcher.fetch(id);
      setAccount(account);
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      if (!account) return;

      if (account.client_tags)
        setTags({
          value: account.client_tags,
          label: account.client_tags,
        });
      if (account.client_source)
        setSources({
          value: account.client_source,
          label: account.client_source,
        });
      if (account.managers?.length) {
        setManagers(
          account.managers.map(manager => ({
            value: manager.value,
            label: manager.label,
          }))
        );
      }
      if (account.account_type) {
        setAccountType({
          value: account.account_type,
          label: resolveAccountTypeTitle(account.account_type),
        });
      }
      if (account.client_out_of_flex) setClientOutOfFlex(account.client_out_of_flex);
      if (account.override_is_new) setIsNew(account.override_is_new);
      if (account.account) setOverrideName(account.account);

      let minDate = account.min_date;
      // @ts-ignore
      let dateFrom = minDate ? toIsoDate(new Date(minDate.split('-')?.reverse())) : defaultDateFrom;
      setFilters({
        dateFrom: dateFrom,
        dateTo: defaultDateTo,
      });
    })();
  }, [account]);

  const resetStatesData = () => {
    if (!account) return;
    setTags(account.tags);
    setSources(account.source);
    setManagers(
      account.managers.map(manager => ({
        value: manager.value,
        label: manager.label,
      }))
    );
    setAccountType(
      account.account_type
        ? {
            value: account.account_type,
            label: resolveAccountTypeTitle(account.account_type),
          }
        : null
    );
  };

  /*
        Handlers
     */
  const updateAccount = async () => {
    if (!id || !account || !managers?.length) return;

    try {
      await editAccountFetcher.fetch({
        accountType: {
          value: account.account_type,
          label: resolveAccountTypeTitle(account.account_type),
        },
        account: account.account,
        managerId: managers[0],
        regionId: { value: account.region, label: account.region },
        clientId: account.client,
        agents: account.agents.map(item => ({
          account: { value: item.account, label: item.account },
          cashback: item.cashback,
        })),
        discount:
          account.account_type === ACCOUNT_TYPE.REFERRAL
            ? account.discount
            : account.mining_terms?.discount !== undefined
              ? account.mining_terms.discount
              : 0,
        cashback:
          account.mining_terms?.cashback !== undefined ? account.mining_terms.cashback : 0,
      });

      if (isNew != account.override_is_new) {
        await switchIsNewFetcher.fetch({ account: account.account, is_new: isNew });
      }

      await customerUpdateFetcher.fetch({
        clientId: account.client_id,
        managers: managers,
        tag: tags,
        source: sources,
        outOfFlex: clientOutOfFlex,
        isNew: isNew,
      });
      setEditing(false);
      setIsSubmitting(false);
      toast.success('Изменения сохранены');
    } catch (e) {
      toast.error('Произошла ошибка при сохранении изменений');
    }
  };

  const onChangeAccountType = async (data: IChangeAccountTypeValues) => {
    if (!account) return;
    if (account.account_type) {
      toast.error('Тип аккаунта уже назначен');
      return;
    }
    if (!data.accountType) {
      toast.error('Выберите тип аккаунта');
      return;
    }

    const manager = managers?.[0] || account.managers[0];
    if (!manager) {
      toast.error('Не удалось определить менеджера аккаунта');
      return;
    }

    try {
      await editAccountFetcher.fetch({
        accountType: data.accountType,
        account: account.account,
        managerId: manager,
        regionId: { value: account.region, label: account.region },
        clientId: account.client,
        discount: data.discount,
        agents: data.agents,
      });

      const refreshed = await accountFetcher.fetch(account.account);
      setAccount(refreshed);
      showChangeTypePopup(false);
      toast.success('Тип аккаунта обновлен');
    } catch (error: any) {
      if (error instanceof ServerError) {
        toast.error(error.error);
      } else {
        toast.error('Произошла ошибка при изменении типа аккаунта');
      }
    }
  };

  const onDeleteAccount = async () => {
    if (!id) return;

    try {
      await deleteAccountFetcher.fetch({ account: id });

      setShowDeleteAccounts(false);
      toast.success('Клиент успешно удален');

      navigate('/account');
    } catch (e) {
      toast.error('Произошла ошибка при удалении');
    }
  };

  return (
    <>
      {!loadingOptions && accountFetcher.isSuccess ? (
        <>
          <Section>
            <div className="text-[24px] flex items-center justify-between">
              <div className="flex gap-[20px] items-center text-[20px]">
                {isEditing ? (
                  <TextInput
                    value={overrideName}
                    width="200px"
                    height="40px"
                    bgColor="#211F24"
                    borderColor="#211F24"
                    onChange={(value: string) => setOverrideName(value)}
                  />
                ) : (
                  <div
                    className={`h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                  >
                    <ValueWithSign
                      containerClassName="[&>*:first-child]:mt-[7px]"
                      value={account?.account}
                      color="bg-[#99840F]"
                    />
                  </div>
                )}
                {account?.client_name ? (
                  <div
                    className={`h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                  >
                    <ValueWithSign
                      containerClassName="[&>*:first-child]:mt-[7px]"
                      value={account?.client_name}
                      color="bg-[#4598E6]"
                    />
                  </div>
                ) : null}
              </div>

              <div className="flex gap-[10px] text-[14px] text-[white] ">
                <div className="flex gap-[5px] items-center">
                  <InputCheckbox
                    label=""
                    onChange={(value: boolean) => {
                      if (isEditing) setClientOutOfFlex(value);
                    }}
                    value={clientOutOfFlex}
                  />
                  <span
                    className={`text-[12px]  text-[#FFFFFF] ${isEditing ? '' : 'opacity-[.4]'} `}
                  >
                    Вне бонуса
                  </span>
                </div>

                {account ? (
                  <>
                    <ToggleSwitch
                      label="Новый клиент"
                      onChange={(value: boolean) => {
                        if (isEditing) setIsNew(value);
                      }}
                      value={isNew}
                    />
                  </>
                ) : null}

                <div
                  className={`h-[33px] min-w-[180px] rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                >
                  <IconWrapper>
                    <IconKanbanUser />
                  </IconWrapper>
                  <span className="ml-[5px]">{managers?.map(value => value.label).join(', ')}</span>
                </div>

                <div
                  className={`h-[33px] min-w-[180px] rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                >
                  <span>{accountType?.label || '-'}</span>
                </div>

                <GrayButton
                  onClick={() => console.log()}
                  title={
                    <div className="flex gap-[10px] items-center fill-[white]">
                      <IconDownload className="w-[11px] h-[14px] fill-[#D9D9D9]" />
                    </div>
                  }
                />
                <PermissionGuard permissions={ACCOUNT_PERMISSIONS.EDIT}>
                  <DropdownButton
                    title={<IconVerticalThreeDots iconWithBorder={false} />}
                    className="h-[33px] w-[15px]"
                    options={accountActions}
                    leftPx={-100}
                    onSelect={(value: any) => {
                      handleAccountAction(value);
                    }}
                  />
                </PermissionGuard>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-[15px] my-[20px]">
              <UserIndicator
                title="Общий хешрейт"
                value={account?.hashrate !== undefined ? account?.hashrate + ' Ph' : '-'}
              />

              <PermissionGuard permissions={ACCOUNT_PERMISSIONS.VIEW_BTC}>
                <UserIndicator
                  title="Общий доход в BTC"
                  value={account?.reward !== undefined ? account?.reward : '-'}
                />
              </PermissionGuard>

              <div className="p-[20px] relative solid rounded-[16px] w-full border border-opacity-30 border-[#959595]">
                <div className="text-[#777777] flex justify-between items-center">
                  Кэшбэк, %
                  <span
                    id="account-terms-tooltip"
                    className="bg-[#211F24] rounded-[100%] w-[12px] h-[12px] text-[8px] text-center cursor-pointer font-bold text-[white]"
                  >
                    !
                  </span>
                  <Tooltip
                    place="top-start"
                    anchorSelect="#account-terms-tooltip"
                    content={
                      account?.account_type === ACCOUNT_TYPE.REFERRAL && (account.agents?.length || 0) > 1
                        ? 'Суммарный кэшбэк по всем агентским аккаунтам клиента, к которым привязан этот реферальный аккаунт.'
                        : 'Кэшбэк реферального аккаунта.'
                    }
                  />
                </div>
                <div className="text-[32px] gap-[10px] flex flex-wrap items-center">
                  {isAdmin ? (
                    <>{referralCashbackTotal !== null ? `${referralCashbackTotal}%` : '-'}</>
                  ) : (
                    <span className="inline-block min-w-[56px] opacity-0 select-none">00%</span>
                  )}
                </div>
              </div>

              <UserIndicator
                title="Дней на Neopool"
                value={account?.days !== undefined ? account?.days : '-'}
              />
              <div className="p-[20px] relative solid rounded-[16px] border border-opacity-30 border-[#959595] w-full">
                <div className="text-[#777777] flex justify-between items-center">
                  Комиссия, %
                  <span
                    id="account-commission-tooltip"
                    className="bg-[#211F24] rounded-[100%] w-[12px] h-[12px] text-[8px] text-center cursor-pointer font-bold text-[white]"
                  >
                    !
                  </span>
                  <Tooltip
                    place="top-start"
                    anchorSelect="#account-commission-tooltip"
                    content="Комиссия аккаунта"
                  />
                </div>
                <div className="text-[32px] flex flex-wrap items-center">
                  {isAdmin ? (
                    <>{account?.referral_interest !== undefined ? account?.referral_interest + '%' : '-'}</>
                  ) : (
                    <span className="inline-block min-w-[56px] opacity-0 select-none">00%</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-[15px] my-[20px] mb-[5px]">
              <div className="w-[100%]">
                {isEditing ? (
                  <>
                    <div className="mb-[10px]">Источник</div>
                    <SingleSelect
                      title="Не выбрано"
                      value={sources}
                      isLoading={false}
                      options={sourceOptions}
                      width="100%"
                      onChange={value => setSources(value)}
                    />
                  </>
                ) : (
                  <>
                    <>
                      <div className="mb-[10px]">Источник</div>
                      <div
                        className={`h-[33px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                      >
                        <span>{sources?.value}</span>
                      </div>
                    </>
                  </>
                )}
              </div>

              <div className="w-[100%]">
                {isEditing ? (
                  <>
                    <div className="mb-[10px]">Тэг</div>
                    <SingleSelect
                      title="Не выбрано"
                      value={tags}
                      isLoading={false}
                      options={tagOptions}
                      width="100%"
                      onChange={value => setTags(value)}
                    />
                  </>
                ) : (
                  <>
                    <>
                      <div className="mb-[10px]">Тэг</div>
                      <div
                        className={`h-[33px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                      >
                        <span>{tags?.value}</span>
                      </div>
                    </>
                  </>
                )}
              </div>

              <div />
              {isEditing ? (
                <>
                  <div className="w-[100%] mt-auto">
                    {!isSubmitting ? (
                      <RedTransparentButton
                        onClick={() => {
                          resetStatesData();
                          setEditing(false);
                        }}
                        title="Отменить"
                      />
                    ) : null}
                  </div>
                  <div className="w-[100%] mt-auto">
                    <BlueTransparentButton
                      onClick={() => {
                        setIsSubmitting(true);
                        if (!isSubmitting) updateAccount();
                      }}
                      title={
                        isSubmitting ? (
                          <Loader size={30} position="center" loading={isSubmitting} />
                        ) : (
                          'Сохранить'
                        )
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div />
                  <div />
                </>
              )}
            </div>

            <div className="mt-8">
              <Section>
                <div className="mb-[30px] flex items-center gap-[20px]">
                  <SectionHeader title="Общий хешрейт" />
                  <CustomRangeDatePicker
                    onChange={values =>
                      setFilters({
                        ...filters,
                        dateFrom: values.startDate,
                        dateTo: values.endDate,
                      })
                    }
                    width={180}
                    shortcuts={dateShortcuts}
                    minDate={filters?.dateFrom ? new Date(filters?.dateFrom) : undefined}
                    /** @ts-ignore */
                    maxDate={
                      reportLastSyncDate
                        ? new Date(reportLastSyncDate.split('.').reverse())
                        : undefined
                    }
                    value={
                      filters
                        ? {
                            startDate: filters.dateFrom,
                            endDate: filters.dateTo,
                          }
                        : undefined
                    }
                  />
                </div>
                {!loadingGraphData ? (
                  <>
                    {graphData.length ? (
                      <>
                        <AccountLinesChart data={graphData} />
                      </>
                    ) : (
                      <div className="mt-[20px]">
                        <EmptyList />
                      </div>
                    )}
                  </>
                ) : (
                  <ListLoader loading={true} />
                )}
              </Section>
            </div>
          </Section>
          {/*
            <accountActions.Component />
            */}
        </>
      ) : (
        <ListLoader loading={true} />
      )}
      {isShowDeleteAccounts ? (
        <DeletePrompt
          onClose={() => {
            setShowDeleteAccounts(false);
          }}
          onSubmit={onDeleteAccount}
          open={true}
          title={<span className="text-[18px] text-[white] text-center">Удалить аккаунт?</span>}
          subTitle={<>Аккаунт будет удалён из базы данных.</>}
        />
      ) : null}
      {isShownChangeTypePopup && account ? (
        <ChangeAccountTypePopup
          account={account}
          onClose={() => showChangeTypePopup(false)}
          onSubmit={onChangeAccountType}
        />
      ) : null}
    </>
  );
};

export default AccountShow;
